/**
 * Pure width math for double-click column auto-sizing, kept free of
 * React/DOM so the clamping rules are unit-testable (same split as
 * columnReorder.ts). The DOM measurement lives in DataGrid.
 */

/** Horizontal room a cell adds around its content (8px padding each side,
 *  plus slack so the widest value doesn't kiss the column edge). */
export const kAutoSizeCellPad = 18;

/** Horizontal room a header adds around its label: left padding plus the
 *  sort caret and filter funnel that share the cell. */
export const kAutoSizeHeaderPad = 40;

export interface AutoSizeMeasurements {
  /** Content widths of the column's rendered cells (unpadded). */
  cellWidths: number[];
  /** Content width of the header label (unpadded). */
  headerWidth: number;
  minSize?: number;
  maxSize?: number;
}

/**
 * Width that fits the widest rendered cell (or the header label) without
 * truncation, clamped to the column's min/max. Mirrors the AG grid's
 * double-click auto-size: only rendered rows are measured.
 */
export function computeAutoSizeWidth({
  cellWidths,
  headerWidth,
  minSize,
  maxSize,
}: AutoSizeMeasurements): number {
  const widestCell = cellWidths.reduce(
    (max, w) => Math.max(max, w + kAutoSizeCellPad),
    0
  );
  const fit = Math.ceil(Math.max(widestCell, headerWidth + kAutoSizeHeaderPad));
  return Math.min(Math.max(fit, minSize ?? 20), maxSize ?? Infinity);
}
